// Iter 82.9 Sprint 2 / Phase E3.1 — Payroll Run Detail (single employee).
//
// Deep-dive view: per-employee compute breakdown with every earning
// row, deduction row, employer contribution row, statutory block,
// snapshots + warnings.  Reachable from /admin/payroll/runs/:rid/employee/:eid.
// Iter 111 · P2.B/C additions:
//   • TDS Health Card (annual liability, ytd, balance, cycles, suggested)
//   • TDS Operator Override modal
//   • LOAN_RECOVERY breakdown (per-loan lines)
//   • Loan EMI Override modal
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Bank, IdentificationCard, MapPin, Calendar, Heartbeat, PencilSimple, Trash, Money, FloppyDisk, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inr0 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function Section({ title, children, testId }) {
    return (
        <section className="border rounded-lg bg-white overflow-hidden" data-testid={testId}>
            <header className="px-4 py-2 border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">{title}</header>
            <div className="p-4">{children}</div>
        </section>
    );
}

function KV({ label, value, mono, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-[10px] uppercase text-slate-400 tracking-wide">{label}</div>
            <div className={`text-sm mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
        </div>
    );
}

function LineRow({ row }) {
    return (
        <tr className="hover:bg-slate-50">
            <td className="px-3 py-1.5 font-mono text-[11px]">{row.component_code}</td>
            <td className="px-3 py-1.5">{row.name}</td>
            <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">{row.formula}</td>
            <td className="px-3 py-1.5 text-right font-mono">{inr(row.raw)}</td>
            <td className="px-3 py-1.5 text-right font-mono">{inr(row.pro_rated)}</td>
            <td className="px-3 py-1.5 text-right font-mono">{inr(row.amount)}</td>
        </tr>
    );
}

export default function PayrollRunDetail() {
    const { rid, eid } = useParams();
    const { can } = useAuth();
    const canOverride = can("payroll:admin");
    const [line, setLine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [empExceptions, setEmpExceptions] = useState([]);
    // Iter 111 · P2.C — TDS Health Card + override
    const [tdsHealth, setTdsHealth] = useState(null);
    const [tdsOverrideOpen, setTdsOverrideOpen] = useState(false);
    const [tdsOverrideAmt, setTdsOverrideAmt] = useState("");
    const [tdsOverrideReason, setTdsOverrideReason] = useState("");
    // Iter 111 · P2.B — Loan Override
    const [loanOverrideOpen, setLoanOverrideOpen] = useState(false);
    const [loanOverrideDraft, setLoanOverrideDraft] = useState({ loan_id: "", amount: "", reason: "" });

    const reload = useCallback(async () => {
        try {
            const [lineRes, excRes, healthRes] = await Promise.all([
                api.get(`/payroll/run/${rid}/lines/${eid}`),
                api.get(`/payroll/run/${rid}/exceptions`),
                api.get(`/payroll/run/${rid}/tds-health/${eid}`).catch(() => ({ data: null })),
            ]);
            setLine(lineRes.data);
            setEmpExceptions((excRes.data.items || []).filter((e) => e.employee_id === eid));
            setTdsHealth(healthRes.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load line"));
        } finally {
            setLoading(false);
        }
    }, [rid, eid]);

    useEffect(() => { reload(); }, [reload]);

    // ── Iter 111 · P2.C — Save TDS override ──────────────────────
    const saveTdsOverride = async () => {
        const amt = Number(tdsOverrideAmt);
        if (!Number.isFinite(amt) || amt < 0) { toast.error("Amount must be ≥ 0"); return; }
        if (!tdsOverrideReason.trim()) { toast.error("Reason is required"); return; }
        try {
            await api.post(`/payroll/run/${rid}/tds-override`, {
                employee_id: eid, amount: amt, reason: tdsOverrideReason.trim(),
            });
            toast.success("TDS override saved. Recalculate the run for it to take effect.");
            setTdsOverrideOpen(false);
            setTdsOverrideAmt(""); setTdsOverrideReason("");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };
    const clearTdsOverride = async () => {
        if (!window.confirm("Clear TDS override for this employee?")) return;
        try {
            await api.post(`/payroll/run/${rid}/tds-override/clear`, { employee_id: eid });
            toast.success("TDS override cleared");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Clear failed"));
        }
    };

    // ── Iter 111 · P2.B — Save loan override ─────────────────────
    const openLoanOverride = (loanId, currentAmount) => {
        setLoanOverrideDraft({
            loan_id: loanId,
            amount: currentAmount == null ? "" : String(currentAmount),
            reason: "",
        });
        setLoanOverrideOpen(true);
    };
    const saveLoanOverride = async () => {
        const amt = Number(loanOverrideDraft.amount);
        if (!Number.isFinite(amt) || amt < 0) { toast.error("Amount must be ≥ 0"); return; }
        if (!loanOverrideDraft.reason.trim()) { toast.error("Reason is required"); return; }
        try {
            await api.post(`/payroll/run/${rid}/loan-override`, {
                employee_id: eid, loan_id: loanOverrideDraft.loan_id,
                amount: amt, reason: loanOverrideDraft.reason.trim(),
            });
            toast.success("Loan override saved. Recalculate the run for it to take effect.");
            setLoanOverrideOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };
    const clearLoanOverride = async (loanId) => {
        if (!window.confirm("Clear loan override?")) return;
        try {
            await api.post(`/payroll/run/${rid}/loan-override/clear`, { employee_id: eid, loan_id: loanId });
            toast.success("Loan override cleared");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Clear failed"));
        }
    };

    if (loading) return <div className="p-6 text-slate-500" data-testid="detail-loading">Loading…</div>;
    if (!line) return <div className="p-6 text-rose-700" data-testid="detail-error">Line not found.</div>;

    const emp = (line.snapshots || {}).employee || {};
    const struct = (line.snapshots || {}).structure || {};
    const att = line.attendance_summary || {};
    const pf = line.pf_block || {};
    const esi = line.esi_block || {};
    const pt = line.pt_block || {};
    const lwf = line.lwf_block || {};
    const tds = line.tds_block || {};
    const loanRow = (line.deductions || []).find((d) => d.component_code === "LOAN_RECOVERY");
    const loanBreakdown = (loanRow && loanRow.loan_breakdown) || [];

    return (
        <div className="space-y-4" data-testid="payroll-run-detail">
            <div className="flex items-center gap-2 text-sm text-slate-500">
                <Link to={`/admin/payroll/runs/${rid}`} className="hover:underline inline-flex items-center gap-1" data-testid="detail-back">
                    <ArrowLeft size={14} /> Back to Run Console
                </Link>
            </div>
            <PageHeader
                eyebrow="Payroll Run Line"
                title={emp.full_name || line.employee_id}
                description={`${emp.employee_code || ""} · ${emp.designation || ""} · ${emp.department || ""}`}
                actions={(
                    <div className="flex items-center gap-2">
                        {line.is_terminal_run && <Badge variant="outline">Terminal (Leaver)</Badge>}
                        {line.joiner_doj && <Badge variant="outline">Joiner</Badge>}
                        {line.is_locked && <Badge className="bg-violet-100 text-violet-800">Locked</Badge>}
                    </div>
                )}
            />

            <Section title="Snapshots (D16 — Immutability Boundary)" testId="detail-snapshots">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KV label="PAN" value={emp.pan} mono testId="snap-pan" />
                    <KV label="Aadhaar (last 4)" value={emp.aadhaar_last4 || "—"} mono testId="snap-aadhaar" />
                    <KV label="Work State" value={emp.work_state} testId="snap-state" />
                    <KV label="Tax Regime" value={emp.tax_regime} testId="snap-regime" />
                    <KV label="Date of Joining" value={emp.date_of_joining} mono testId="snap-doj" />
                    <KV label="Last Working Day" value={emp.last_working_day || "—"} mono testId="snap-lwd" />
                    <KV label="Bank A/c" value={emp.bank?.account_number ? `…${String(emp.bank.account_number).slice(-4)}` : "—"} mono testId="snap-bank" />
                    <KV label="IFSC" value={emp.bank?.ifsc || "—"} mono testId="snap-ifsc" />
                    <KV label="Structure" value={`${struct.code || ""} v${struct.version_no || "?"}`} mono testId="snap-structure" />
                    <KV label="Assignment ID" value={(line.snapshots || {}).assignment_id} mono testId="snap-assignment" />
                </div>
            </Section>

            <Section title="Attendance Summary" testId="detail-attendance">
                {att.attendance_summary_system && (
                    <div className="mb-3 p-3 border border-amber-200 bg-amber-50 rounded text-xs" data-testid="attendance-system-note">
                        <div className="font-semibold text-amber-900 mb-1">System (Frozen) vs Current (After Admin Adjustment)</div>
                        <div className="text-amber-800">
                            This employee has an active PLAF ATTENDANCE adjustment. The frozen
                            values from attendance snapshots are shown below in <em>italic</em>; the
                            current (post-adjustment) values the engine uses are shown bold on top.
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-sm" data-testid="attendance-current-row">
                    <KV label="Month Days" value={att.month_days} mono />
                    <KV label="Working Days" value={att.working_days} mono />
                    <KV label="Paid Days" value={<span className="font-semibold">{att.paid_days}</span>} mono />
                    <KV label="LOP Days" value={<span className="font-semibold">{att.lop_days}</span>} mono />
                    <KV label="Present" value={att.present_days} mono />
                    <KV label="Half Days" value={att.half_days} mono />
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-sm mt-2" data-testid="attendance-current-row-2">
                    <KV label="Leave (Paid)" value={att.leave_days_paid} mono />
                    <KV label="Leave (Unpaid)" value={att.leave_days_unpaid} mono />
                    <KV label="Holidays" value={att.holidays} mono />
                    <KV label="Weekly Offs" value={att.weekly_offs} mono />
                    <KV label="Pro-rata" value={`${(line.pro_rata_fraction * 100).toFixed(2)}%`} mono />
                    <KV label="LOP Multiplier" value={`${(line.lop_multiplier * 100).toFixed(2)}%`} mono />
                </div>
                {(Number(att.pending_regularization_days || 0) - Number(att.pending_days_paid || 0)) > 0 && Number(att.lop_days || 0) > 0 && (
                    <div className="mt-3 p-3 border border-amber-200 bg-amber-50 rounded text-xs text-amber-900" data-testid="attendance-provisional-lop-note">
                        <span className="font-semibold">Provisional LOP:</span>{" "}
                        {Number(att.pending_regularization_days || 0) - Number(att.pending_days_paid || 0)} of the LOP days are
                        Pending Regularization days, provisionally counted as unpaid until a Pay Pending
                        decision is approved. The underlying attendance records remain Pending Regularization
                        and are not rewritten.
                    </div>
                )}
                {Number(att.pending_days_paid || 0) > 0 && (
                    <div className="mt-3 p-3 border border-emerald-200 bg-emerald-50 rounded text-xs text-emerald-900" data-testid="attendance-pending-paid-note">
                        <span className="font-semibold">Pending days paid:</span>{" "}
                        {Number(att.pending_days_paid || 0)} Pending Regularization day(s) are paid via an
                        audited Pay Pending decision; the attendance records themselves remain Pending
                        Regularization.
                    </div>
                )}
                {att.attendance_summary_system && (
                    <div className="mt-3 pt-3 border-t border-dashed border-slate-200" data-testid="attendance-system-row">
                        <div className="text-[10px] uppercase text-slate-400 tracking-wide mb-1">System (Frozen Attendance — pre-PLAF)</div>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-sm italic text-slate-600">
                            <KV label="Paid Days" value={att.attendance_summary_system.paid_days} mono />
                            <KV label="LOP Days" value={att.attendance_summary_system.lop_days} mono />
                            <KV label="Present" value={att.attendance_summary_system.present_days} mono />
                            <KV label="Half Days" value={att.attendance_summary_system.half_days} mono />
                            <KV label="Weekly Offs" value={att.attendance_summary_system.weekly_offs} mono />
                            <KV label="Holidays" value={att.attendance_summary_system.holidays} mono />
                        </div>
                    </div>
                )}
            </Section>

            <Section title="Earnings" testId="detail-earnings">
                <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500">
                        <tr>
                            <th className="px-3 py-1.5 text-left">Code</th>
                            <th className="px-3 py-1.5 text-left">Name</th>
                            <th className="px-3 py-1.5 text-left">Formula</th>
                            <th className="px-3 py-1.5 text-right">Raw</th>
                            <th className="px-3 py-1.5 text-right">Pro-rated</th>
                            <th className="px-3 py-1.5 text-right">Final Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {(line.earnings || []).map((r) => <LineRow key={r.component_code} row={r} />)}
                    </tbody>
                    <tfoot className="border-t-2">
                        <tr><td colSpan={5} className="px-3 py-2 text-right font-semibold">Gross Earnings</td><td className="px-3 py-2 text-right font-mono font-semibold" data-testid="detail-gross">{inr0(line.gross_earnings)}</td></tr>
                    </tfoot>
                </table>
            </Section>

            <Section title="Deductions (Employee Side)" testId="detail-deductions">
                <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500">
                        <tr>
                            <th className="px-3 py-1.5 text-left">Code</th>
                            <th className="px-3 py-1.5 text-left">Name</th>
                            <th className="px-3 py-1.5 text-left">Formula</th>
                            <th className="px-3 py-1.5 text-right">Raw</th>
                            <th className="px-3 py-1.5 text-right">Pro-rated</th>
                            <th className="px-3 py-1.5 text-right">Final Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {(line.deductions || []).map((r) => <LineRow key={r.component_code} row={r} />)}
                    </tbody>
                    <tfoot className="border-t-2">
                        <tr><td colSpan={5} className="px-3 py-2 text-right font-semibold">Gross Deductions</td><td className="px-3 py-2 text-right font-mono font-semibold text-rose-700" data-testid="detail-deductions-total">{inr0(line.gross_deductions)}</td></tr>
                    </tfoot>
                </table>
            </Section>

            <Section title="Employer Contributions (CTC, not paid)" testId="detail-employer">
                <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500">
                        <tr>
                            <th className="px-3 py-1.5 text-left">Code</th>
                            <th className="px-3 py-1.5 text-left">Name</th>
                            <th className="px-3 py-1.5 text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {(line.employer_contributions || []).map((r) => (
                            <tr key={r.component_code}>
                                <td className="px-3 py-1.5 font-mono text-[11px]">{r.component_code}</td>
                                <td className="px-3 py-1.5">{r.name}</td>
                                <td className="px-3 py-1.5 text-right font-mono">{inr(r.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Section title="PF Block" testId="detail-pf">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <KV label="Basic (Capped)" value={inr(pf.basic_capped)} mono />
                        <KV label="EE Share" value={inr(pf.ee)} mono />
                        <KV label="ER Share" value={inr(pf.er)} mono />
                        <KV label="Admin Charges" value={inr(pf.admin_charges)} mono />
                        {pf.wage_ceiling_applied && <Badge variant="outline" className="col-span-2 text-[10px]">Wage Ceiling Applied</Badge>}
                    </div>
                </Section>
                <Section title="ESI Block" testId="detail-esi">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <KV label="Applicable" value={esi.applicable ? "Yes" : "No"} mono />
                        <KV label="EE Share" value={inr(esi.ee)} mono />
                        <KV label="ER Share" value={inr(esi.er)} mono />
                        <KV label="Gross Considered" value={inr(esi.gross_capped)} mono />
                    </div>
                </Section>
                <Section title={`PT (${pt.state || "—"})`} testId="detail-pt">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <KV label="State" value={pt.state} />
                        <KV label="Monthly Tax" value={inr(pt.amount)} mono />
                    </div>
                </Section>
                <Section title="LWF" testId="detail-lwf">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <KV label="Applicable" value={lwf.applicable ? "Yes" : "No"} mono />
                        <KV label="EE Share" value={inr(lwf.ee)} mono />
                        <KV label="ER Share" value={inr(lwf.er)} mono />
                        <KV label="Frequency" value={lwf.frequency || "—"} />
                    </div>
                </Section>
            </div>

            <Section title={`TDS — ${(tds.regime || "new").toUpperCase()} regime`} testId="detail-tds">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <KV label="FY Months Elapsed" value={tds.fy_months_elapsed} mono />
                    <KV label="FY Months Remaining" value={tds.fy_months_remaining} mono />
                    <KV label="Current Month Taxable" value={inr(tds.current_month_taxable)} mono />
                    <KV label="YTD Taxable (Prior)" value={inr(tds.ytd_taxable_before_current)} mono />
                    <KV label="Projected Annual Income" value={inr(tds.projected_annual_income)} mono />
                    <KV label="Standard Deduction" value={inr(tds.standard_deduction_applied)} mono />
                    <KV label="Slab Tax" value={inr(tds.slab_tax)} mono />
                    <KV label="87A Rebate" value={inr(tds.rebate_87a)} mono />
                    <KV label="Cess (4%)" value={inr(tds.cess)} mono />
                    <KV label="Annual Tax" value={inr(tds.annual_tax)} mono />
                    <KV label="YTD TDS Deducted" value={inr(tds.ytd_tds_deducted_before_current)} mono />
                    <KV label="Monthly TDS (this run)" value={inr(tds.monthly_tds)} mono />
                </div>
            </Section>

            {/* Iter 111 · P2.C — TDS Health Card ─────────────────── */}
            {tdsHealth && (
                <Section title={
                    <span className="flex items-center gap-2">
                        <Heartbeat size={16} className="text-rose-500" />
                        TDS Health · {tdsHealth.regime?.toUpperCase()} regime
                        {tdsHealth.is_final_cycle && <Badge className="bg-amber-100 text-amber-900 text-[10px]">Final Cycle (true-up)</Badge>}
                    </span>
                } testId="detail-tds-health">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <KV label="Annual Liability" value={inr(tdsHealth.annual_liability)} mono testId="tds-annual-liability" />
                        <KV label="YTD Deducted" value={inr(tdsHealth.ytd_deducted)} mono testId="tds-ytd-deducted" />
                        <KV label="Balance Remaining" value={inr(tdsHealth.balance_remaining)} mono testId="tds-balance-remaining" />
                        <KV label="Cycles Remaining" value={tdsHealth.cycles_remaining} mono testId="tds-cycles-remaining" />
                        <KV label="Suggested This Cycle" value={inr(tdsHealth.suggested_recovery_this_cycle)} mono testId="tds-suggested" />
                    </div>
                    <div className="mt-3 flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div>
                            <div className="text-[11px] uppercase text-slate-500 font-semibold">Engine-computed TDS</div>
                            <div className="text-sm font-mono">{inr(tdsHealth.engine_computed)}</div>
                            {tdsHealth.current_override && (
                                <div className="mt-2">
                                    <div className="text-[11px] uppercase text-indigo-700 font-semibold">Current override</div>
                                    <div className="text-sm font-mono">{inr(tdsHealth.current_override.amount)}</div>
                                    <div className="text-[11px] text-slate-500 italic mt-0.5 max-w-md">{tdsHealth.current_override.reason}</div>
                                </div>
                            )}
                        </div>
                        {canOverride && (
                            <div className="flex flex-col gap-2">
                                <Button size="sm" onClick={() => {
                                    setTdsOverrideAmt(String(tdsHealth.current_override?.amount ?? tdsHealth.suggested_recovery_this_cycle ?? ""));
                                    setTdsOverrideReason(tdsHealth.current_override?.reason || "");
                                    setTdsOverrideOpen(true);
                                }} data-testid="tds-override-open">
                                    <PencilSimple size={12} className="mr-1" /> {tdsHealth.current_override ? "Edit Override" : "Set Override"}
                                </Button>
                                {tdsHealth.current_override && (
                                    <Button size="sm" variant="outline" onClick={clearTdsOverride} data-testid="tds-override-clear">
                                        <Trash size={12} className="mr-1" /> Clear
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {/* Iter 111 · P2.B — Loan Recovery breakdown ────────────── */}
            {loanBreakdown.length > 0 && (
                <Section title={
                    <span className="flex items-center gap-2">
                        <Money size={16} className="text-emerald-500" />
                        Loan Recovery Breakdown · {inr(loanRow.amount)}
                    </span>
                } testId="detail-loan-recovery">
                    <div className="overflow-x-auto border border-slate-200 rounded">
                        <table className="w-full text-xs" data-testid="loan-recovery-table">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                    <th className="text-left px-2 py-1.5">Loan ID</th>
                                    <th className="text-left px-2 py-1.5">Policy</th>
                                    <th className="text-left px-2 py-1.5">Status</th>
                                    <th className="text-right px-2 py-1.5">EMI</th>
                                    <th className="text-right px-2 py-1.5">Principal</th>
                                    <th className="text-right px-2 py-1.5">Interest</th>
                                    <th className="text-right px-2 py-1.5">Instalment</th>
                                    <th className="text-left px-2 py-1.5">Override</th>
                                    {canOverride && <th className="text-right px-2 py-1.5">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {loanBreakdown.map((b) => (
                                    <tr key={b.loan_id} className="border-t border-slate-100" data-testid={`loan-line-${b.loan_id}`}>
                                        <td className="px-2 py-1.5 font-mono text-[11px]">{b.loan_id}</td>
                                        <td className="px-2 py-1.5">{b.policy_code}</td>
                                        <td className="px-2 py-1.5">
                                            <Badge variant="outline" className="text-[10px]">{b.loan_status}</Badge>
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono">{inr(b.amount)}</td>
                                        <td className="px-2 py-1.5 text-right font-mono">{b.principal_portion == null ? "—" : inr(b.principal_portion)}</td>
                                        <td className="px-2 py-1.5 text-right font-mono">{b.interest_portion == null ? "—" : inr(b.interest_portion)}</td>
                                        <td className="px-2 py-1.5 text-right font-mono">
                                            {b.instalment_no == null ? "—" : `${b.instalment_no} / ${b.total_instalments}`}
                                        </td>
                                        <td className="px-2 py-1.5">
                                            {b.is_override ? (
                                                <span className="inline-flex items-center gap-1">
                                                    <Badge className="bg-indigo-100 text-indigo-800 text-[10px]">Override</Badge>
                                                    <span className="text-[10px] text-slate-500 italic">{b.override_reason}</span>
                                                </span>
                                            ) : <span className="text-slate-400 text-[10px]">—</span>}
                                        </td>
                                        {canOverride && (
                                            <td className="px-2 py-1.5 text-right">
                                                <Button size="sm" variant="ghost" onClick={() => openLoanOverride(b.loan_id, b.amount)} data-testid={`loan-override-btn-${b.loan_id}`}>
                                                    <PencilSimple size={12} />
                                                </Button>
                                                {b.is_override && (
                                                    <Button size="sm" variant="ghost" onClick={() => clearLoanOverride(b.loan_id)} data-testid={`loan-override-clear-${b.loan_id}`}>
                                                        <Trash size={12} />
                                                    </Button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>
            )}

            <Section title="Net Pay Computation" testId="detail-netpay">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <KV label="Gross Earnings" value={inr(line.gross_earnings)} mono />
                    <KV label="Gross Deductions" value={inr(line.gross_deductions)} mono />
                    <KV label="Net (Unrounded)" value={inr(line.net_pay_unrounded)} mono />
                    <KV label="Round-Off" value={inr(line.round_off)} mono />
                    <KV label="Net Pay (₹1 round)" value={inr0(line.net_pay)} mono testId="detail-net-pay" />
                    <KV label="CTC for Period" value={inr(line.ctc_for_period)} mono />
                </div>
            </Section>

            {empExceptions.length > 0 && (
                <Section title="Exceptions on this Employee" testId="detail-exceptions">
                    <ul className="space-y-1.5 text-sm">
                        {empExceptions.map((e) => (
                            <li key={e.id} className="flex items-start gap-2">
                                <Badge variant="outline" className="text-[10px]">{e.severity}</Badge>
                                <span className="font-mono text-[11px] text-slate-500">{e.category}</span>
                                <span className="flex-1">{e.message}</span>
                                <Badge variant="outline" className="text-[10px]">{e.status}</Badge>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {(line.computation_warnings || []).length > 0 && (
                <Section title="Computation Warnings" testId="detail-warnings">
                    <ul className="space-y-1 text-sm text-amber-800">
                        {(line.computation_warnings || []).map((w, i) => <li key={i}>⚠ {w}</li>)}
                    </ul>
                </Section>
            )}

            {/* Iter 111 · P2.C — TDS Override modal ─────────────── */}
            <Dialog open={tdsOverrideOpen} onOpenChange={setTdsOverrideOpen}>
                <DialogContent className="max-w-md" data-testid="tds-override-dialog">
                    <DialogHeader>
                        <DialogTitle>TDS Operator Override</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-xs text-slate-600">
                            The override replaces the engine-computed TDS for this employee on this run. The engine&apos;s computed number is preserved on the payslip for audit.
                        </div>
                        <div>
                            <Label>Override amount (₹)</Label>
                            <Input type="number" step="1" value={tdsOverrideAmt} onChange={(e) => setTdsOverrideAmt(e.target.value)} data-testid="tds-override-amount" />
                        </div>
                        <div>
                            <Label>Reason *</Label>
                            <Textarea rows={3} value={tdsOverrideReason} onChange={(e) => setTdsOverrideReason(e.target.value)} placeholder="e.g. 80C proofs received post-run; adjusting to match declared regime" data-testid="tds-override-reason" />
                        </div>
                        <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                            Remember to click <b>Recalculate</b> on the run for the override to appear on the payslip.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setTdsOverrideOpen(false)}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={saveTdsOverride} data-testid="tds-override-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter 111 · P2.B — Loan Override modal ─────────────── */}
            <Dialog open={loanOverrideOpen} onOpenChange={setLoanOverrideOpen}>
                <DialogContent className="max-w-md" data-testid="loan-override-dialog">
                    <DialogHeader>
                        <DialogTitle>Loan EMI Operator Override · {loanOverrideDraft.loan_id}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-xs text-slate-600">
                            The override supersedes the scheduled EMI amount for this cycle only. Subsequent cycles revert to the schedule unless a new override is posted.
                        </div>
                        <div>
                            <Label>Override amount (₹)</Label>
                            <Input type="number" step="1" value={loanOverrideDraft.amount} onChange={(e) => setLoanOverrideDraft({ ...loanOverrideDraft, amount: e.target.value })} data-testid="loan-override-amount" />
                        </div>
                        <div>
                            <Label>Reason *</Label>
                            <Textarea rows={3} value={loanOverrideDraft.reason} onChange={(e) => setLoanOverrideDraft({ ...loanOverrideDraft, reason: e.target.value })} placeholder="e.g. temporary hardship reduction requested by employee" data-testid="loan-override-reason" />
                        </div>
                        <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                            Remember to click <b>Recalculate</b> on the run for the override to reach the payslip.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setLoanOverrideOpen(false)}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={saveLoanOverride} data-testid="loan-override-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
